import React, { useContext, useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";

import api from "../../services/api";
import officialCampaignService from "../../services/officialCampaignService";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
  templateEditor: {
    fontFamily: "monospace",
    minHeight: 200
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1)
  },
  offlineDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    display: "inline-block",
    marginRight: theme.spacing(1)
  }
}));

const defaultForm = {
  name: "",
  contactListId: "",
  tagIds: [],
  whatsappId: "",
  ticketRegister: false,
  ticketStatus: "closed",
  ticketQueueId: "",
  ticketUserId: "",
  scheduledAt: "",
  templateName: "",
  templateLanguage: "",
  templateComponentsJson: "[]"
};

const OfficialCampaignModal = ({ open, onClose, onSuccess }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [form, setForm] = useState(defaultForm);
  const [contactLists, setContactLists] = useState([]);
  const [tags, setTags] = useState([]);
  const [queues, setQueues] = useState([]);
  const [users, setUsers] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const companyId = user?.companyId;

  useEffect(() => {
    if (!open || !companyId) {
      // Reset form quando modal fechar
      if (!open) {
        setForm(defaultForm);
        setSelectedTemplate(null);
        setTemplates([]);
      }
      return;
    }

    const fetchData = async () => {
      try {
        const [{ data: lists }, { data: tagList }, { data: queueData }, { data: usersData }, { data: whatsappData }] = await Promise.all([
          api.get("/contact-lists/list", { params: { companyId } }),
          api.get("/tags/list", { params: { companyId, kanban: 0 } }),
          api.get("/queue"),
          api.get("/users/"),
          api.get("/whatsapp", { params: { companyId, session: 0 } })
        ]);

        setContactLists(lists || []);
        setTags(tagList || []);
        setQueues(queueData || []);
        setUsers(usersData?.users || []);
        
        const officials = (whatsappData || []).filter(w => w.channel === "whatsapp_oficial");
        setWhatsapps(officials);
      } catch (err) {
        toastError(err);
      }
    };

    fetchData();
  }, [open, companyId]);

  const handleChange = useCallback((field) => (event) => {
    const value = event?.target?.value;
    if (field === "ticketRegister") {
      setForm(prev => ({ ...prev, ticketRegister: event.target.checked }));
    } else {
      setForm(prev => ({ ...prev, [field]: value }));
    }
  }, []);

  const handleSelectTemplate = useCallback((template) => {
    setSelectedTemplate(template);
    setForm(prev => ({
      ...prev,
      templateName: template?.name || "",
      templateLanguage: template?.language || "",
      templateComponentsJson: JSON.stringify(template?.components || [], null, 2)
    }));
  }, []);

  const handleLoadTemplates = useCallback(async (whatsappId) => {
    if (!whatsappId) {
      setTemplates([]);
      return;
    }
    setLoadingTemplates(true);
    try {
      const { data } = await officialCampaignService.templates(whatsappId);
      setTemplates(data?.data || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  const estimateContacts = useMemo(() => {
    if (form.contactListId) {
      const selected = contactLists.find(list => list.id === form.contactListId);
      // Tentar diferentes campos que podem conter o count
      if (selected?.contactsCount !== undefined) return selected.contactsCount;
      if (selected?.total !== undefined) return selected.total;
      if (Array.isArray(selected?.contacts)) return selected.contacts.length;
      // Se não tiver, retornar null para indicar que precisa buscar
      return null;
    }

    if (form.tagIds?.length) {
      const total = form.tagIds.reduce((acc, tagId) => {
        const tag = tags.find(item => item.id === tagId);
        if (!tag) return acc;
        if (Array.isArray(tag.contacts)) {
          return acc + tag.contacts.length;
        }
        if (tag.count !== undefined) return acc + tag.count;
        return acc;
      }, 0);
      return total > 0 ? total : null;
    }

    return null;
  }, [form.contactListId, form.tagIds, contactLists, tags]);

  const estimatedDuration = useMemo(() => {
    if (!estimateContacts || estimateContacts === null) return "—";
    // Usar intervalo padrão de 20 segundos (mesmo das configurações de campanha)
    const intervalSeconds = 20;
    const totalSeconds = estimateContacts * intervalSeconds;
    const minutes = Math.ceil(totalSeconds / 60);
    if (minutes < 1) return "< 1 min";
    return `${minutes} min`;
  }, [estimateContacts]);

  const connectionStatus = useMemo(() => {
    const conn = whatsapps.find(item => item.id === form.whatsappId);
    if (!conn) return null;
    return conn.status;
  }, [whatsapps, form.whatsappId]);

  const resetForm = () => {
    setForm(defaultForm);
    setSelectedTemplate(null);
    setTemplates([]);
  };

  const handleSubmit = async () => {
    try {
      if (!form.whatsappId || !selectedTemplate) {
        toast.error("Selecione uma conexão e um template.");
        return;
      }

      const templateComponents = JSON.parse(form.templateComponentsJson || "[]");

      const payload = {
        name: form.name || selectedTemplate.name,
        contactListId: form.contactListId || null,
        tagIds: form.tagIds,
        whatsappId: form.whatsappId,
        templateName: selectedTemplate.name,
        templateLanguage: selectedTemplate.language,
        templateComponents,
        ticketRegister: form.ticketRegister,
        ticketStatus: form.ticketStatus || "closed",
        ticketQueueId: form.ticketQueueId || null,
        ticketUserId: form.ticketUserId || null,
        scheduledAt: form.scheduledAt || null
      };

      setLoading(true);
      const { data } = await officialCampaignService.create(payload);
      await officialCampaignService.schedule(data.id, {
        scheduledAt: form.scheduledAt || null
      });

      toast.success("Campanha oficial agendada com sucesso!");
      resetForm();
      onSuccess?.();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Nova campanha oficial</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography className={classes.sectionTitle}>Público-alvo & Conexão</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Nome da campanha"
              fullWidth
              value={form.name}
              onChange={handleChange("name")}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="contact-list-label">Lista de Contatos</InputLabel>
              <Select
                labelId="contact-list-label"
                value={form.contactListId}
                onChange={handleChange("contactListId")}
              >
                <MenuItem value="">
                  <em>Selecione</em>
                </MenuItem>
                {contactLists.map(list => (
                  <MenuItem key={list.id} value={list.id}>
                    {list.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="tags-label">Tags (múltiplas)</InputLabel>
              <Select
                labelId="tags-label"
                multiple
                value={form.tagIds}
                onChange={handleChange("tagIds")}
                renderValue={selected => (
                  <Box display="flex" flexWrap="wrap" gap={8}>
                    {selected.map(value => {
                      const tag = tags.find(t => t.id === value);
                      return <Chip key={value} label={tag?.name || value} size="small" />;
                    })}
                  </Box>
                )}
              >
                {tags.map(tag => (
                  <MenuItem key={tag.id} value={tag.id}>
                    {tag.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="whatsapp-label">Conexão oficial</InputLabel>
              <Select
                labelId="whatsapp-label"
                value={form.whatsappId}
                onChange={(event) => {
                  const value = event.target.value;
                  setForm(prev => ({ ...prev, whatsappId: value }));
                  handleLoadTemplates(value);
                }}
              >
                <MenuItem value="">
                  <em>Selecione</em>
                </MenuItem>
                {whatsapps.map(w => (
                  <MenuItem key={w.id} value={w.id}>
                    {w.name || w.phone_number}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {form.whatsappId && (
              <Box mt={1}>
                <span
                  className={classes.offlineDot}
                  style={{
                    backgroundColor: connectionStatus === "CONNECTED" ? "#4caf50" : "#f44336"
                  }}
                />
                <Typography variant="body2" component="span">
                  {connectionStatus === "CONNECTED" ? "Online" : "Offline"}
                </Typography>
              </Box>
            )}
          </Grid>

          <Grid item xs={12}>
            <Typography className={classes.sectionTitle}>Configurações de Tickets</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.ticketRegister}
                  onChange={handleChange("ticketRegister")}
                  color="primary"
                />
              }
              label="Registrar no sistema"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="ticket-status-label">Status do ticket</InputLabel>
              <Select
                labelId="ticket-status-label"
                value={form.ticketStatus}
                onChange={handleChange("ticketStatus")}
              >
                <MenuItem value="closed">Encerrado</MenuItem>
                <MenuItem value="pending">Pendente</MenuItem>
                <MenuItem value="open">Aberto</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="queue-label">Fila</InputLabel>
              <Select
                labelId="queue-label"
                value={form.ticketQueueId}
                onChange={handleChange("ticketQueueId")}
              >
                <MenuItem value="">
                  <em>Sem fila</em>
                </MenuItem>
                {queues.map(queue => (
                  <MenuItem key={queue.id} value={queue.id}>
                    {queue.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="user-label">Usuário responsável</InputLabel>
              <Select
                labelId="user-label"
                value={form.ticketUserId}
                onChange={handleChange("ticketUserId")}
              >
                <MenuItem value="">
                  <em>Automático</em>
                </MenuItem>
                {users.map(u => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="datetime-local"
              label="Agendar execução"
              InputLabelProps={{ shrink: true }}
              value={form.scheduledAt}
              onChange={handleChange("scheduledAt")}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography className={classes.sectionTitle}>Template da campanha</Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="template-label">Selecione o template</InputLabel>
              <Select
                labelId="template-label"
                value={selectedTemplate?.name || ""}
                onChange={(event) => {
                  const templateName = event.target.value;
                  const template = templates.find(t => t.name === templateName);
                  if (template) {
                    handleSelectTemplate(template);
                  }
                }}
                disabled={!templates.length || loadingTemplates}
              >
                <MenuItem value="">
                  <em>{loadingTemplates ? "Carregando..." : "Selecione"}</em>
                </MenuItem>
                {templates.map(template => (
                  <MenuItem key={template.id} value={template.name}>
                    {template.name} ({template.language})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Componentes do template (JSON)"
              multiline
              fullWidth
              minRows={6}
              variant="outlined"
              value={form.templateComponentsJson}
              onChange={handleChange("templateComponentsJson")}
              className={classes.templateEditor}
            />
            <Typography variant="caption" color="textSecondary">
              Use placeholders como {"{{contact.name}}"} para personalizar dinamicamente.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Box mt={2} p={2} border={1} borderColor="divider" borderRadius={8}>
              <Typography className={classes.sectionTitle}>Resumo</Typography>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Total estimado de contatos</Typography>
                <Typography variant="body1" color="primary">
                  {estimateContacts !== null ? `${estimateContacts} contatos` : "—"}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Tempo estimado</Typography>
                <Typography variant="body1" color="primary">
                  {estimatedDuration}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">WhatsApp</Typography>
                <Typography variant="body1" color="primary">
                  {whatsapps.find(w => w.id === form.whatsappId)?.name || "—"}
                </Typography>
              </Box>
              <Typography variant="caption" color="textSecondary">
                A campanha será executada imediatamente após salvar (ou no horário programado).
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="default">
          Cancelar
        </Button>
        <Button color="primary" variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? "Salvando..." : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OfficialCampaignModal;

